import React, { Component } from 'react';
import { connect } from 'react-redux';
import {
  withRouter,
  ContextRouter,
} from 'react-router-dom';
import queryString from 'query-string';

import { loginOwner } from '../../../../../../actions/owner/loginOwner';
import { addPreferredWalker } from '../../../../../../actions/owner/addPreferredWalker';
import {
  resetRequest,
} from '../../../../../../actions/requests';
import Envelope from '../../../../../../assets/svg/Envelope';
import Lock from '../../../../../../assets/svg/Lock';
import {
  composeValidators, Field, Form, Validate,
} from '../../../../../../components/Form';
import InputGroup from '../../../../../../components/Form/InputGroup/InputGroup';
import ButtonWithSpinner from '../../../../../../components/ButtonWithSpinner';
import { getRequestStates, getRequestErrorMessage } from '../../../../../../selectors';
import style from './WagLoginForm.module.css';

import { withHistory, WithHistoryInjectedProps } from '../../../../../../hocs';

import {
  OWNER_TOKEN_JWT_REQUEST_KEY,
  QS_REDIRECT_URL_KEY,
  ROUTE_HOME,
} from '../../../../../../constants';

type FormValues = {
  email: string,
  password: string,
};

const validate = (values: FormValues) => ({
  email: composeValidators(Validate.required, Validate.email)(values.email),
  password: Validate.required(values.password),
});

type WrapperProps = {
  token: string,
  error: boolean,
  isFetching: boolean,
  isSuccess: boolean,
  onLoginOwner: typeof loginOwner,
  onResetRequest: typeof resetRequest,
  errorMessage: string,
  ownerID?: string,
};

type Props = WrapperProps & WithHistoryInjectedProps & ContextRouter;

class WagLoginForm extends Component<Props> {
  constructor(props) {
    super(props);

    const {
      onResetRequest,
    } = this.props;

    if (onResetRequest) {
      onResetRequest(OWNER_TOKEN_JWT_REQUEST_KEY);
    }
  }

  componentDidUpdate(prevProps) {
    const {
      isSuccess,
      history: {
        push,
        location: {
          search,
        },
      },
    } = this.props;

    const {
      [QS_REDIRECT_URL_KEY]: redirectUrlQueryString,
      ...otherQueryString
    } = queryString.parse(search);
    const queryStringToForward = queryString.stringify(otherQueryString);

    const redirectUrl = redirectUrlQueryString || ROUTE_HOME;

    if (!prevProps.isSuccess && isSuccess) {
      this.addPreferredWalkerByPromoCode();
      push({ path: redirectUrl, search: queryStringToForward, retainQueryString: false });
    }
  }

  /**
   * Adds a preferred walker to the owner logging in by a promo_code specified in the URL.
   * If there is a promo_code in the URL, this is likely because a user is attempting to login
   * and book a service with a specific Petcare Giver when coming from their public profile.
   */
  addPreferredWalkerByPromoCode = () => {
    const {
      onAddPreferredWalker,
      location: {
        search,
      },
    } = this.props;
    const parsedQueryString = queryString.parse(search);
    const promoCode = parsedQueryString.promo_code;

    if (promoCode) {
      onAddPreferredWalker({
        walkerPromoCode: promoCode,
      });
    }
  }

  handleSubmit = (values: FormValues) => { // eslint-disable-line
    const {
      onLoginOwner,
    } = this.props;

    const errors = validate(values);

    if (Object.values(errors).some(error => !!error)) {
      return errors;
    }

    const { email, password } = values;

    // call wag API login
    onLoginOwner({
      username: email,
      password,
    });
  };

  render() {
    const {
      errorMessage,
      error,
      isFetching,
    } = this.props;

    return (
      <div>
        <Form
          onSubmit={this.handleSubmit}
          validate={validate}
          render={({
            handleSubmit,
            getSubmitButtonProps,
            setValues,
          }) => (
            <form onSubmit={handleSubmit}>
              <Field name="email">

                {({ getInputProps }) => (
                  <InputGroup
                    placeholder="Email"
                    disabled={isFetching}
                    prepend={(
                      <Envelope
                        color="#616a9b"
                        width="30"
                      />
                    )}
                    data-testid="email-input"
                    {...getInputProps({
                      onBlur: e => setValues({
                        [e.currentTarget.name]: e.currentTarget.value.replace(/\s/g, ''),
                      }),
                    })}
                  />
                )}
              </Field>

              <Field name="password">
                {({ getInputProps }) => (
                  <InputGroup
                    placeholder="Password"
                    disabled={isFetching}
                    prepend={(
                      <Lock
                        color="#616a9b"
                        width="30"
                      />
                    )}
                    type="password"
                    data-testid="password-input"
                    {...getInputProps()}
                  />
                )}
              </Field>

              {error && (
                <div
                  className={style.alert}
                  data-testid="login-error-alert"
                >
                  {(typeof errorMessage === 'string' && errorMessage)
                    || 'An unexpected error has occurred. Please try again.'}
                </div>
              )}

              <div className={style.formGroup}>
                <ButtonWithSpinner
                  {...getSubmitButtonProps({ isFetching })}
                  disabled={isFetching}
                  value="Login"
                  buttonStyle="secondary"
                  data-testid="login-submit"
                />
              </div>
            </form>
          )}
        />
      </div>
    );
  }
}


export default withRouter<any>(withHistory(connect(state => ({
  errorMessage: getRequestErrorMessage(state, OWNER_TOKEN_JWT_REQUEST_KEY),
  ...getRequestStates(state, OWNER_TOKEN_JWT_REQUEST_KEY),
}), {
  onAddPreferredWalker: addPreferredWalker,
  onLoginOwner: loginOwner,
  onResetRequest: resetRequest,
})(WagLoginForm)));
